import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, FileText, Lock, Send } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

const STRUCTURE = [
  ["SPV", "LRG Leather Botswana"],
  ["Capital Requirement", "$45M USD"],
  ["Jurisdiction", "Botswana SEZA"],
  ["Project Lead", "LRG Holdings"],
  ["Land", "35 ha · Lobatse SEZ"],
  ["Capacity", "1,500 hides / day"],
];

const REVENUE_STREAMS = [
  { code: "01", label: "Tannery output", body: "Wet-blue and crust leather sold into Italian and EU finishing markets.", share: "~30%" },
  { code: "02", label: "Finished goods", body: "Footwear, handbags and accessories produced under Italian-grade specifications.", share: "~45%" },
  { code: "03", label: "Lease & manufacturing", body: "Long-term leases to strategic manufacturing partners inside the Lobatse SEZ.", share: "~15%" },
  { code: "04", label: "Training & licensing", body: "Italian master-tanner programmes and technology licensing income.", share: "~5%" },
  { code: "05", label: "Export distribution", body: "Direct EU/US distribution margin via offtake and distribution agreements.", share: "~5%" },
];

const PHASES = [
  { when: "Q4 2026", title: "Land Finalisation", body: "Title transfer · master site planning · EIA finalised." },
  { when: "Q2 2027", title: "Construction Begins", body: "Tannery shell · Italian equipment procurement begins." },
  { when: "Q4 2027", title: "Phase 1 Production", body: "Tannery commissioning · initial luxury manufacturing lines online." },
  { when: "Q2 2028", title: "Export Scale", body: "Full export volume to EU/US · Phase 2 expansion." },
];

const SEZA_ADVANTAGES = [
  "Special Economic Zone tax framework",
  "Industrial land allocation at scale",
  "Export-driven licensing structure",
  "Skills transfer & local hiring incentives",
  "Botswana value-addition mandate alignment",
  "Stable currency & democratic governance",
];

export default function Portal() {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Inquiry form
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    organization: user?.organization || "",
    investor_type: user?.investor_type || "",
    capital_range: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/project-status")
      .then((r) => setStatuses(r.data))
      .catch(() => setStatuses([]))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/inquiries", form);
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="page-portal" className="bg-[#F9F9F6] pt-32 md:pt-40">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="grid grid-cols-12 gap-8 items-end pb-12 border-b border-[#1A1A1A]">
          <div className="col-span-12 md:col-span-8">
            <p className="eyebrow inline-flex items-center gap-2">
              <Lock size={12} className="text-[#5C2018]" />
              Investor Portal
            </p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-5">
              Welcome, <span className="italic text-[#5C2018]">{user?.name?.split(" ")[0] || "Investor"}.</span>
            </h1>
            <p className="mt-4 text-sm text-[#666666]">
              {user?.organization && <span>{user.organization} · </span>}
              {user?.email}
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <p className="eyebrow">Project</p>
            <p className="font-serif text-2xl tracking-tight mt-2">LRG Leather Botswana</p>
            <p className="text-xs text-[#666666] mt-2">SPV · SEZA-aligned · Lobatse</p>
          </div>
        </div>

        {/* Project structure */}
        <section className="py-16 md:py-24" data-testid="portal-structure">
          <p className="eyebrow">01. Project Structure</p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-4 mb-10">The headline numbers.</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 border-l border-t border-[#1A1A1A]">
            {STRUCTURE.map(([k, v]) => (
              <div key={k} className="border-r border-b border-[#1A1A1A] p-6 md:p-8 bg-[#F9F9F6] hover:bg-white transition-colors">
                <p className="eyebrow">{k}</p>
                <p className="font-serif text-2xl md:text-3xl tracking-tight mt-4">{v}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Live status */}
        <section className="py-16 md:py-24 border-t border-[#E5E5E0]" data-testid="portal-status">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="eyebrow">02. Live Project Status</p>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-4">Where we are today.</h2>
            </div>
            <p className="text-xs text-[#666666]">Updated by LRG Holdings · {new Date().toLocaleDateString()}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
            {loading ? (
              <p className="p-8 text-sm text-[#666666] bg-[#F9F9F6]">Loading status…</p>
            ) : (
              statuses.map((s) => (
                <div key={s.id} data-testid={`portal-status-${s.id}`} className="bg-[#F9F9F6] p-6 md:p-8">
                  <div className="flex items-center justify-between">
                    <p className="eyebrow text-[#5C2018]">{s.category}</p>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#666666]">{s.status}</span>
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl tracking-tight mt-3 leading-tight">{s.title}</h3>
                  <p className="text-sm text-[#666666] mt-2 leading-relaxed">{s.note}</p>
                  <div className="mt-5">
                    <div className="h-px w-full bg-[#E5E5E0] relative">
                      <div className="absolute inset-y-0 left-0 bg-[#5C2018]" style={{ width: `${s.progress || 0}%` }} />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-[#666666]">
                      <span>{s.progress || 0}%</span>
                      {s.target_date && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar size={11} /> {s.target_date}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Revenue */}
        <section className="py-16 md:py-24 border-t border-[#E5E5E0]" data-testid="portal-revenue">
          <p className="eyebrow">03. Projected Revenue Streams</p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-4 mb-10">Five income channels.</h2>
          <div className="border-t border-[#1A1A1A]">
            {REVENUE_STREAMS.map((r) => (
              <div key={r.code} className="grid grid-cols-12 gap-6 py-8 border-b border-[#E5E5E0]">
                <div className="col-span-2 md:col-span-1 font-serif text-2xl text-[#5C2018]">{r.code}</div>
                <div className="col-span-10 md:col-span-3">
                  <h3 className="font-serif text-xl tracking-tight">{r.label}</h3>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <p className="text-sm text-[#1A1A1A] leading-relaxed">{r.body}</p>
                </div>
                <div className="col-span-12 md:col-span-2 md:text-right font-serif text-2xl text-[#1A1A1A]">{r.share}</div>
              </div>
            ))}
          </div>
        </section>

        {/* SEZA + phases */}
        <section className="py-16 md:py-24 border-t border-[#E5E5E0] grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          <div>
            <p className="eyebrow">04. SEZA Advantages</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-4 mb-8">Why this is bankable.</h2>
            <ul className="space-y-4">
              {SEZA_ADVANTAGES.map((a) => (
                <li key={a} className="flex items-start gap-3 border-b border-[#E5E5E0] pb-4">
                  <span className="font-serif text-[#5C2018]">+</span>
                  <span className="text-sm leading-relaxed">{a}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="eyebrow">05. Phases</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-4 mb-8">From land to export.</h2>
            <ol className="border-l border-[#1A1A1A] pl-6 space-y-6">
              {PHASES.map((p) => (
                <li key={p.when}>
                  <p className="font-serif text-xl text-[#5C2018]">{p.when}</p>
                  <p className="font-serif text-lg mt-1">{p.title}</p>
                  <p className="text-sm text-[#666666] mt-1 leading-relaxed">{p.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Documents placeholder */}
        <section className="py-16 md:py-20 border-t border-[#E5E5E0]" data-testid="portal-documents">
          <p className="eyebrow">06. Investor Materials</p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-4 mb-8">Documents on request.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
            {[
              ["Information Memorandum", "Detailed SPV structure, capital flow, risk and return."],
              ["Financial Model", "5-year projections · scenario analysis · IRR logic."],
              ["Project Charter", "Land, licensing, partners, milestones."],
            ].map(([t, b]) => (
              <div key={t} className="bg-[#F9F9F6] p-6 md:p-8">
                <FileText size={20} className="text-[#5C2018]" />
                <h3 className="font-serif text-xl mt-4 tracking-tight">{t}</h3>
                <p className="text-sm text-[#666666] mt-2 leading-relaxed">{b}</p>
                <p className="mt-6 text-[10px] uppercase tracking-[0.22em] text-[#666666]">Available on direct request</p>
              </div>
            ))}
          </div>
        </section>

        {/* Submit interest */}
        <section className="py-16 md:py-24 border-t border-[#E5E5E0]" data-testid="portal-inquiry">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <p className="eyebrow">07. Engage</p>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-4 leading-tight">
                Request a call or submit interest.
              </h2>
              <p className="mt-5 text-sm text-[#666666] leading-relaxed">
                Your message is logged in the LRG investor pipeline. A senior team member will follow up within five business days.
              </p>
            </div>
            <div className="md:col-span-7">
              {submitted ? (
                <div data-testid="portal-inquiry-success" className="border border-[#5C2018] p-8">
                  <p className="eyebrow text-[#5C2018]">Received</p>
                  <p className="font-serif text-2xl mt-3 tracking-tight">Your message is in the LRG pipeline.</p>
                  <p className="mt-3 text-sm text-[#666666]">We will respond directly via {form.email}.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5" data-testid="portal-inquiry-form">
                  <SmallField label="Capital range" className="md:col-span-2">
                    <input
                      type="text"
                      value={form.capital_range}
                      onChange={change("capital_range")}
                      data-testid="portal-inquiry-capital"
                      className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 focus:outline-none focus:border-[#5C2018]"
                      placeholder="e.g. $1M-$5M"
                    />
                  </SmallField>
                  <SmallField label="Message" className="md:col-span-2">
                    <textarea
                      rows={4}
                      required
                      value={form.message}
                      onChange={change("message")}
                      data-testid="portal-inquiry-message"
                      className="mt-2 w-full border border-[#E5E5E0] p-3 focus:outline-none focus:border-[#5C2018]"
                      placeholder="Areas of interest, time horizon, ticket size…"
                    />
                  </SmallField>
                  {error && (
                    <p data-testid="portal-inquiry-error" className="md:col-span-2 text-sm text-[#5C2018] border-l-2 border-[#5C2018] pl-3">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="portal-inquiry-submit"
                    className="md:col-span-2 inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#5C2018] text-[#F9F9F6] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A] transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Submit to LRG"}
                    <Send size={14} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>

      <section className="bg-[#1A1A1A] text-[#F9F9F6] mt-12">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-16 grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
          <div>
            <p className="eyebrow text-[#A8A8A0]">Continue</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-3">Review the financial model.</h2>
          </div>
          <div className="md:text-right">
            <Link
              to="/financial-model"
              data-testid="portal-to-model"
              className="inline-flex items-center gap-3 px-7 py-4 bg-[#F9F9F6] text-[#1A1A1A] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#5C2018] hover:text-[#F9F9F6] transition-colors"
            >
              Financial Model
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function SmallField({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="eyebrow">{label}</label>
      {children}
    </div>
  );
}
